import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';

export default function AdminProvisionTeacher() {
  const { session, profile } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [classroomName, setClassroomName] = useState('Homeroom');
  const [sendMagic, setSendMagic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accessToken = session?.access_token || null;
  const isAdmin = ['admin', 'superadmin'].includes(profile?.role ?? '');

  if (!accessToken) {
    return <div className="p-6">You must be signed in.</div>;
  }

  if (!isAdmin) {
    return <div className="p-6">You do not have permission to view this page.</div>;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accessToken) return;

    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const targetEmail = email.trim().toLowerCase();
      const targetName = fullName.trim();

      const response = await fetch('/api/v1/admin/teachers/provision', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: targetEmail,
          full_name: targetName,
          classroom_name: classroomName,
          send_magic_link: sendMagic,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.error || 'Provisioning failed';
        throw new Error(message);
      }

      try {
        await fetch('/api/v1/admin/allowlist', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            action: 'grant',
            email: targetEmail,
            role: 'teacher',
            name: targetName,
          }),
        });
      } catch (allowlistError) {
        console.warn('allowlist grant failed', allowlistError);
      }

      setResult(data);
    } catch (err: unknown) {
      setError((err as Error | null)?.message || 'Provisioning failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-2">Provision Teacher &amp; Classroom</h1>
      <p className="text-sm text-cocoa/70 mb-6">
        Create a teacher account and default classroom in one step.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Teacher Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="teacher@example.org"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Full Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Jane Smith"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Classroom Name</label>
          <input
            type="text"
            value={classroomName}
            onChange={(e) => setClassroomName(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Homeroom"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="sendMagic"
            type="checkbox"
            checked={sendMagic}
            onChange={(e) => setSendMagic(e.target.checked)}
          />
          <label htmlFor="sendMagic" className="text-sm">
            Send magic link (recommended)
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-terracotta text-white px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {submitting ? 'Provisioning…' : 'Create Teacher & Classroom'}
          </button>
          <button
            type="button"
            className="text-sm text-cocoa/70"
            onClick={() => navigate('/admin')}
          >
            Back to Admin
          </button>
        </div>
      </form>

      {error && <div className="mt-4 text-red-600 text-sm">{error}</div>}

      {result && Boolean(result.ok) && (
        <div className="mt-6 p-4 border rounded-lg bg-green-50 text-sm">
          <div>✅ Teacher provisioned.</div>
          <div>
            User ID: <span className="font-mono">{String(result.userId ?? '')}</span>
          </div>
          <div>
            Role: <span className="font-mono">{String(result.profileRole ?? '')}</span>
          </div>
          <div>
            Classroom ID: <span className="font-mono">{String(result.classroomId ?? '')}</span>
          </div>
          {Boolean(result.action_link) && (
            <div className="mt-2">
              <div className="mb-1">Magic link (copy &amp; send to teacher):</div>
              <input
                readOnly
                className="w-full border rounded px-2 py-1 font-mono text-xs"
                value={String(result.action_link ?? '')}
                onFocus={(e) => e.target.select()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
